import React, { useContext, useState } from "react";
import styled, { useTheme } from "styled-components";
import { FuturesAsSpotNotice } from "features/bots/BotForm/Advanced/FollowFuturesAsSpot";
import { scrollToFuturesAsSpotToggle } from "features/bots/BotForm/Advanced/futuresAsSpotScroll";
import { FollowFuturesAsSpotData } from "@shared/features/bots/BotForm/common/hooks/useFollowFuturesAsSpot";
import AmountSelector from "features/bots/BotForm/General/AmountSelector";
import useMarginForm from "features/bots/BotForm/General/useMarginForm";
import useBotNameInput from "features/bots/BotForm/General/useBotNameInput";
import useSymbolAndAccountSelectors from "features/bots/BotForm/common/useSymbolAndAccountSelectors";
import { FormFieldTitle } from "features/bots/common/styles";
import EqualColumnsGrid from "common/Grids/EqualColumnsGrid";
import useMeasure from "utils/hooks/useMeasure";
import useGeneral from "@shared/features/bots/BotForm/General/useGeneral";
import { getCoinExtraDetailsRoute } from "utils/routes";
import { useHistory } from "react-router-dom";
import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context";
import { OpenInNewIcon } from "common/Icons/Icons";
import { isSymbol } from "@shared/types/utils";
import { BotFormType, Steps } from "@shared/features/bots/BotForm/common/types";
import MultiSymbolMarginSelect from "features/bots/BotForm/MultiSymbolMargin/MultiSymbolMarginSelect.tsx";
import { BotType, LanguageNameSpaces } from "@shared/types";
import GridsSelector from "features/bots/BotForm/Strategies/GridsSelector";
import RestoreChangesButton from "features/bots/BotForm/common/RestoreChangesButton";
import Modal from "common/Modal/Modal";
import { SectionTitle } from "features/bots/BotForm/common/styles";
import Checkbox from "common/Checkbox/Checkbox";
import { HelperTooltip } from "common/Tooltips/HelperTooltip";
import useFormHelperData from "@shared/features/bots/BotForm/common/hooks/useFormHelperData";
import SubGroupSelect from "features/bots/BotForm/General/SubGroupSelect";
import useQuery from "utils/hooks/useQuery";
import NoExchangeConnectedModal from "features/configuration/common/NoExchangeConnectedModal";
import Tooltip from "common/Tooltips/Tooltip";
import Text from "common/Typography/Text";
import { ACCOUNT_ID_QUERY_PARAM } from "features/bots/common/constants.ts";
import CreatePersonalGroupModal from "features/bots/Groups/CreatePersonalGroupModal.tsx";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import ExchangesTags from "features/bots/BotForm/General/ExchangesTags.tsx";
import Link from "common/Typography/Link.tsx";
import { typography } from "@shared/common/Themes/typography.ts";
import useSignalSymbolAndExchangesSelectors from "features/tradingTerminal/General/useSignalSymbolAndExchangesSelectors.tsx";
import SymbolFromAlertToggle from "features/bots/BotForm/General/SymbolFromAlertToggle.tsx";
import { CheckboxChangeEvent } from "antd/es/checkbox";
import { BOT_FORM_ACCOUNTS_CLEAR_BUTTON_ID } from "testIds.ts";
import DirectionFieldsRow from "common/Grids/DirectionFieldsRow";
import NoAccountConnectCard from "features/bots/BotForm/General/NoAccountConnectCard";
import useShowNoAccountCard from "features/bots/BotForm/common/useShowNoAccountCard";
import NoGroupSignalsSuggestion from "features/bots/BotForm/common/NoGroupSignalsSuggestion";

const General = ({
  futuresAsSpot,
}: {
  futuresAsSpot?: FollowFuturesAsSpotData;
}) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const history = useHistory();
  const query = useQuery();
  const {
    symbols,
    botType,
    isAdvancedView,
    setIsAdvancedView,
    setTab,
    isChannelConfiguration,
    botFormType,
    setAccounts,
    setExchanges,
  } = useContext(BotFormContext)!;
  const isSignalsBot = botType === BotType.SIGNAL;
  const isGridBot = botType === BotType.GRID;
  const isHedgingBot = botType === BotType.HEDGING;
  const isSingleSymbol = !isSymbol(symbols) && !isSignalsBot && !!symbols[0];
  const botAccountId = query.get(ACCOUNT_ID_QUERY_PARAM);
  const queryAccountId = botAccountId ? Number(botAccountId) : undefined;
  const isTradingViewSignalsBot = botType === BotType.TRADING_VIEW_SIGNALS;
  const isBulkCreate = botFormType === BotFormType.BULK_CREATE;
  const showClearExchanges = isBulkCreate || isTradingViewSignalsBot;

  const { getHelperData } = useFormHelperData();
  const { isNewLook } = useTheme();
  const [measureRef, { width }] = useMeasure<HTMLDivElement>();
  const [openPersonalGroupModal, setOpenPersonalGroupModal] = useState(false);
  const showNoAccountCard = useShowNoAccountCard();

  const {
    numColumns,
    marginFormData,
    symbolAndAccountSelectorsData,
    nameInputData,
    leveragedExchangesSelectorData,
  } = useGeneral({
    useMarginForm,
    useSymbolAndAccountSelectors: isTradingViewSignalsBot
      ? undefined
      : useSymbolAndAccountSelectors,
    useSignalSymbolAndExchangesSelectors: isTradingViewSignalsBot
      ? useSignalSymbolAndExchangesSelectors
      : undefined,
    useBotNameInput,
    width,
    queryAccountId,
  });

  const {
    accountSelector,
    accountError,
    symbolSelector,
    symbolError,
    accountSelectorTitle,
    symbolSelectorTitle,
    noAccountModalOpen,
    closeNoAccountModal,
    getAccountErrorMessage,
    getExchangeErrorMessage,
  } = symbolAndAccountSelectorsData || {};
  const {
    showLeverageSection,
    longShortSelect,
    marginTypeSelect,
    multiplierSelect,
    warning: leverageWarning,
    gridBotDirectionChangeModalData,
    leverageWarningModal,
  } = marginFormData;

  const getSymbolSelector = () => {
    if ((isSignalsBot && !isAdvancedView) || isChannelConfiguration) return;

    return (
      <div key="symbols">
        <FormFieldTitle onClick={isSingleSymbol ? openSymbolDrawer : undefined}>
          {symbolSelectorTitle}
          {isSingleSymbol && (
            <Tooltip title={t("tooltips.view_symbol_details")}>
              <StyledOpenInNewIcon />
            </Tooltip>
          )}
          {isSignalsBot && <HelperTooltip {...getHelperData("symbols")} />}
        </FormFieldTitle>
        {symbolSelector}
        {symbolError}
        <SymbolFromAlertToggle />
      </div>
    );
  };

  const getAccountsSelector = () => {
    if (!accountSelector) return;
    return (
      <div
        key="accounts"
        style={showNoAccountCard ? { gridColumn: "1 / -1" } : undefined}
      >
        <FormFieldTitle style={{ justifyContent: "space-between" }}>
          {accountSelectorTitle}
          {showClearExchanges && (
            <Link
              data-testid={BOT_FORM_ACCOUNTS_CLEAR_BUTTON_ID}
              onClick={() =>
                isTradingViewSignalsBot ? setExchanges([]) : setAccounts([])
              }
              style={{ fontSize: typography.fontSizes.S }}
            >
              Clear
            </Link>
          )}
        </FormFieldTitle>
        {showNoAccountCard ? <NoAccountConnectCard /> : accountSelector}
        {futuresAsSpot?.showNotice && (
          <FuturesAsSpotNotice
            skipsSpotSignals={futuresAsSpot.skipsSpotSignals}
            onManage={() => {
              setIsAdvancedView(true);
              setTab(Steps.ADVANCED);
              scrollToFuturesAsSpotToggle();
            }}
          />
        )}
        {isBulkCreate && (
          <ExchangesTags
            getAccountErrorMessage={getAccountErrorMessage}
            getExchangeErrorMessage={getExchangeErrorMessage}
          />
        )}
        {!showNoAccountCard && accountError}
      </div>
    );
  };

  const getAccountsAndSymbolsSelectors = () => {
    const selectors = [getAccountsSelector(), getSymbolSelector()];
    if (isTradingViewSignalsBot) selectors.reverse();
    return selectors;
  };
  const getGridsSelector = () => {
    if (isHedgingBot) return;
    if (!isGridBot) return;
    return <GridsSelector style={{ marginTop: 16 }} isGeneralTab={true} />;
  };

  const getDirectionChangeModal = () => {
    return (
      <Modal
        open={gridBotDirectionChangeModalData.isOpen}
        title={gridBotDirectionChangeModalData.title}
        onOk={gridBotDirectionChangeModalData.onOk}
        onCancel={gridBotDirectionChangeModalData.onCancel}
      >
        {gridBotDirectionChangeModalData.content}
      </Modal>
    );
  };

  const getLeveragedExchangesSelector = () => {
    if (!leveragedExchangesSelectorData.shouldDisplay) return;
    return (
      <div style={{ marginTop: 16 }}>
        <Checkbox
          checked={leveragedExchangesSelectorData.value}
          onChange={(e: CheckboxChangeEvent) =>
            leveragedExchangesSelectorData.onChange(e.target.checked)
          }
        >
          <Text>{leveragedExchangesSelectorData.title}</Text>
        </Checkbox>
      </div>
    );
  };

  const getLeverageSelectors = () => {
    if (!showLeverageSection) return;
    if (isHedgingBot) {
      return (
        <>
          <EqualColumnsGrid numColumns={2} style={{ marginTop: 16 }}>
            {marginTypeSelect}
            {multiplierSelect}
          </EqualColumnsGrid>
          {leverageWarning}
        </>
      );
    }
    return (
      <>
        {!isSignalsBot && longShortSelect ? (
          <DirectionFieldsRow
            direction={longShortSelect}
            fields={
              <>
                {marginTypeSelect}
                {multiplierSelect}
              </>
            }
            style={{ marginTop: 16 }}
          />
        ) : (
          <EqualColumnsGrid numColumns={2} style={{ marginTop: 16 }}>
            {longShortSelect}
            {!isSignalsBot ? (
              <>
                {marginTypeSelect}
                {multiplierSelect}
              </>
            ) : (
              <div />
            )}
          </EqualColumnsGrid>
        )}
        {leverageWarning}
      </>
    );
  };

  const openSymbolDrawer = () => {
    return history.push(
      getCoinExtraDetailsRoute(location.pathname, symbols[0])
    );
  };

  const getCreateAccountModal = () => {
    if (!closeNoAccountModal) return;
    return (
      <NoExchangeConnectedModal
        placement="bot_form"
        open={noAccountModalOpen}
        onClose={closeNoAccountModal}
        action={t("buttons.create_bot")}
        redirectOnClick={false}
      />
    );
  };

  return (
    <div style={{ position: "relative" }}>
      <RestoreChangesButton
        tab={Steps.GENERAL}
        title={<SectionTitle>{t("tabs.general")}</SectionTitle>}
      />
      <div style={{ opacity: width || isChannelConfiguration ? 1 : 0 }}>
        {!isChannelConfiguration && (
          <>
            <SelectWrapper>{nameInputData.nameInput}</SelectWrapper>
            <EqualColumnsGrid
              numColumns={
                isBulkCreate || (isNewLook && width < NEW_LOOK_TWO_COLUMNS)
                  ? 1
                  : numColumns
              }
              ref={measureRef}
            >
              <SubGroupSelect
                setOpenPersonalGroupModal={setOpenPersonalGroupModal}
              />
              {getAccountsAndSymbolsSelectors()}
            </EqualColumnsGrid>
            <NoGroupSignalsSuggestion />
          </>
        )}
        {getGridsSelector()}
        <div style={{ marginTop: isChannelConfiguration ? 0 : 16 }}>
          <AmountSelector />
        </div>
        {!isHedgingBot && getLeveragedExchangesSelector()}
        {getLeverageSelectors()}
        {isSignalsBot && (
          <EqualColumnsGrid
            numColumns={1}
            style={{ display: showLeverageSection ? undefined : "none" }}
          >
            <MultiSymbolMarginSelect
              style={{
                marginTop: 16,
              }}
            />
          </EqualColumnsGrid>
        )}
      </div>
      {getDirectionChangeModal()}
      {getCreateAccountModal()}
      <CreatePersonalGroupModal
        open={openPersonalGroupModal}
        closeModal={() => setOpenPersonalGroupModal(false)}
      />
      {leverageWarningModal}
    </div>
  );
};

export default React.memo(General);

/** New look: the account and symbol sit side by side from this width. */
const NEW_LOOK_TWO_COLUMNS = 440;

const SelectWrapper = styled.div`
  &:not(:last-of-type) {
    margin-bottom: 20px;
    ${(p) => p.theme.isNewLook && `margin-bottom: 16px;`}
  }
`;
const StyledOpenInNewIcon = styled(OpenInNewIcon)`
  margin-left: 8px;
  width: 10px;
  fill: ${(p) => p.theme.colors.primaryGrey};
`;
